import type { CalculationSettingsUploadResult } from '@/api';
import FileUploadModal from '@/components/file-upload/file-upload-modal';
import RequiredFormatTooltip from '@/components/file-upload/required-format-tooltip';
import UploadDropZone from '@/components/file-upload/upload-drop-zone';
import type { FileUploadResult } from '@/components/file-upload/use-file-upload';
import { Flex } from '@lego/klik-ui';
import { type RefObject, useState } from 'react';
import {
  useGetCalculationSettingsPresignedPutUrl,
  useGetUploadReport,
} from '../calculation-settings-queries';
import DownloadButton from './download-button';

export type CalcSettingsUploadResult = FileUploadResult;

type CalcSettingsUploadModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  title: string;
  description: string;
  target: string;
  initialFile?: File;
  onClose: () => void;
  onUploadFinished: (result: CalcSettingsUploadResult) => void;
};

const CalcSettingsUploadModal = ({
  isOpen,
  triggerRef,
  title,
  description,
  target,
  initialFile,
  onClose,
  onUploadFinished,
}: CalcSettingsUploadModalProps) => {
  const { mutateAsync } = useGetUploadReport();
  const { mutateAsync: getPresignedPutUrl } =
    useGetCalculationSettingsPresignedPutUrl(target);
  const [uploadResult, setUploadResult] = useState<
    CalculationSettingsUploadResult | undefined
  >();

  return (
    <FileUploadModal
      isOpen={isOpen}
      triggerRef={triggerRef}
      title={`Upload — ${title}`}
      target={target}
      getPresignedPutUrl={getPresignedPutUrl}
      getReport={async (fileId: string) => {
        const response = await mutateAsync(fileId);
        setUploadResult(response.uploadResult);
        return response.fileProcessResult;
      }}
      contentHeight="380px"
      initialFile={initialFile}
      onClose={onClose}
      onUploadFinished={onUploadFinished}
      successMessage={
        <div style={{ textAlign: 'left' }}>
          <b>{uploadResult?.rowsAdded ?? 0}</b>{' '}
          {uploadResult?.rowsAdded === 1 ? 'row' : 'rows'} added
          <br />
          <b>{uploadResult?.rowsUpdated ?? 0}</b> updated
          <br />
          <b>{uploadResult?.rowsRemoved ?? 0}</b> removed
        </div>
      }
      renderIdle={({ onBrowseClick }) => (
        <>
          <Flex flexDirection="column" gap="8px">
            <span className="body-md-medium" style={{ fontWeight: 700 }}>
              {title}
            </span>
            <span className="body-md-regular">
              Use the provided <DownloadButton target={target} inline /> to
              upload calculation settings. Files must match the{' '}
              <RequiredFormatTooltip columns={description} />.
            </span>
          </Flex>
          <UploadDropZone onBrowseClick={onBrowseClick} />
        </>
      )}
    />
  );
};

export default CalcSettingsUploadModal;
